/**
 * Time string validation and clamping utilities.
 *
 * These work with "HH:MM" (24-hour) formatted strings.
 */

const TIME_REGEX = /^(\d{1,2}):(\d{2})$/;

/**
 * Returns true when `str` matches `H:MM` or `HH:MM` **and** the hour is 0-23
 * and the minute is 0-59.
 */
export function isValidTime(str: string): boolean {
  const match = str.match(TIME_REGEX);
  if (!match) return false;
  const h = parseInt(match[1]!, 10);
  const m = parseInt(match[2]!, 10);
  return h >= 0 && h <= 23 && m >= 0 && m <= 59;
}

/**
 * If `str` matches the `H:MM` / `HH:MM` pattern, clamp hours to 0-23 and
 * minutes to 0-59 and return the normalised `"HH:MM"` string.
 *
 * Returns `null` when the input doesn't match the expected pattern at all.
 */
export function clampTime(str: string): string | null {
  const match = str.match(TIME_REGEX);
  if (!match) return null;
  const h = Math.min(23, Math.max(0, parseInt(match[1]!, 10)));
  const m = Math.min(59, Math.max(0, parseInt(match[2]!, 10)));
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

/**
 * Formats a partially-typed time input into `HH:MM` as the user types.
 *
 * Strips non-digits, keeps at most four digits, clamps the hour segment to
 * `00`–`23` and the minute segment to `00`–`59`, and auto-inserts `:` once
 * the second hour digit has been entered. Backspacing across the colon
 * collapses back to the raw hour digits so the user can keep editing.
 */
export function formatTimeTyping(input: string): string {
  const digits = input.replace(/\D/g, "").slice(0, 4);
  if (digits.length < 2) return digits;

  const hh = Math.min(23, parseInt(digits.slice(0, 2), 10));
  const hhStr = hh.toString().padStart(2, "0");
  if (digits.length === 2) return hhStr;

  const mmRaw = digits.slice(2);
  if (mmRaw.length === 1) {
    // Only 0–5 can lead to a valid 2-digit minute; clamp so the user
    // can't type themselves into an invalid state.
    const first = parseInt(mmRaw, 10);
    return `${hhStr}:${first > 5 ? "5" : mmRaw}`;
  }
  const mm = Math.min(59, parseInt(mmRaw, 10));
  return `${hhStr}:${mm.toString().padStart(2, "0")}`;
}
